import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { CustomButton } from '../../MUI_custom_component';
import { useSelector } from 'react-redux';
import TreePlantedCard from './Components/TreePlantedCard';
import {
  getTreePlantationAction,
  downloadTreeCertificateAction,
} from './APIs/action';
import IconReusable from '../../../components/IconReusable';

const TreePlantation = ({}) => {
  const { selectedEntity: selectedCompanyEntity } = useSelector(
    (state) => state.entityReducer,
  );
  const CHIP_STYLES = {
    Birthday: { borderColor: 'green', backgroundColor: 'rgba(0,128,0,0.12)' },
    Anniversary: {
      borderColor: '#0081f2',
      backgroundColor: 'rgba(0,129,242,0.12)',
    },
  };
  const isMobile = useMediaQuery('(max-width:1200px)');

  const [treeData, setTreeData] = useState(null);
  const [loader, setLoader] = useState(false);
  const [openTreeId, setOpenTreeId] = useState(null);

  const fetchPlantationData = async () => {
    setLoader(true);
    const res = await getTreePlantationAction(selectedCompanyEntity);
    setLoader(false);
    if (!res?.error) {
      setTreeData(res?.data);
    }
  };

  useEffect(() => {
    if (selectedCompanyEntity) fetchPlantationData();
  }, [selectedCompanyEntity]);

  if (loader) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Grid
        container
        spacing={{ xs: 2, sm: 2, md: 3, lg: 3 }}
        p={2}
      >
        <Grid
          item
          xs={12}
          sm={6}
          lg={6}
        >
          <Typography
            sx={{ mb: { xs: 0.5, md: 1 } }}
            variant='h2'
          >
            {treeData?.user_name}'s Tree
          </Typography>

          {treeData?.plant_list?.map((item, index) => (
            <TreePlantedCard
              data={item}
              key={item.tree_id}
              isOpen={openTreeId === item.tree_id}
              isLast={index === treeData.plant_list.length - 1}
              onToggle={() =>
                setOpenTreeId((prev) =>
                  prev === item.tree_id ? null : item.tree_id,
                )
              }
            />
          ))}
        </Grid>

        {treeData?.history?.length > 0 && (
          <Grid
            item
            xs={12}
            sm={6}
            lg={6}
          >
            <Typography
              sx={{ mb: { xs: 0.5, md: 1 } }}
              variant='h2'
            >
              Your trees planted
            </Typography>
            <TableContainer
              component={Paper}
              variant='outlined'
              sx={{
                border: 'none',
                boxShadow: 'none',
                borderRadius: '16px',
                overflowX: 'auto',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: '#E5E5E5',
                      '& .MuiTableCell-root': {
                        borderRight: 'none',
                        borderBottom: '1px solid #8C8C8C',
                        py: 2,
                        px: 2,
                      },
                    }}
                  >
                    <TableCell align='center'>
                      <Typography variant='labelM'>Occasion</Typography>
                    </TableCell>
                    <TableCell align='center'>
                      <Typography variant='labelM'>Planted Date</Typography>
                    </TableCell>
                    <TableCell align='center'>
                      <Typography variant='labelM'>eCertificate</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {treeData?.history?.map((tree, index) => (
                    <TableRow
                      key={tree.tree_id || index}
                      sx={{
                        backgroundColor: '#fff',
                        boxShadow: 'inset 0 1px 0 0 #D9D9D9',
                        '& .MuiTableCell-root': {
                          borderBottom: 'none',
                          borderRight: 'none',
                          py: 1,
                          px: 2,
                        },
                      }}
                    >
                      <TableCell align='center'>
                        {(() => {
                          const occ = tree.occasion
                            ? tree.occasion.charAt(0).toUpperCase() +
                              tree.occasion.slice(1).toLowerCase()
                            : 'Birthday';
                          const chipStyle =
                            CHIP_STYLES[occ] || CHIP_STYLES.Birthday;
                          return (
                            <Chip
                              label={occ}
                              size='small'
                              variant='outlined'
                              sx={{
                                ...chipStyle,
                                color: '#333',
                                fontFamily: 'Lato',
                                fontWeight: 600,
                                fontSize: '13px',
                                height: '24px',
                                borderRadius: '32px',
                              }}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell align='center'>
                        <Typography variant='p3'>
                          {tree.planted_date
                            ? new Date(tree.planted_date).toLocaleDateString(
                                'en-GB',
                                {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                },
                              )
                            : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align='center'>
                        <IconButton
                          sx={{
                            order: { xs: 1, md: 2 },
                            alignSelf: { xs: 'end' },
                          }}
                          onClick={() =>
                            downloadTreeCertificateAction(tree.tree_id)
                          }
                        >
                          <IconReusable
                            name='DownloadOutlined'
                            color='#995A00'
                            size={isMobile ? '20px' : '24px'}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default TreePlantation;
